import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Facebook, Twitter, MessageCircle, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import EmptyState from "../components/EmptyState";
import { Reveal } from "../components/Reveal";
import api from "../lib/api";
import { fmtDate } from "../lib/format";

function youtubeEmbed(url) {
  const m = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [portrait, setPortrait] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <EmptyState title="Article introuvable" message="Cet article n'existe pas ou n'est plus publié." testId="article-not-found" />
          <Link to="/actualites" className="mt-8 inline-flex items-center gap-2 text-rouge font-heading uppercase tracking-wider text-sm">
            <ArrowLeft size={16} /> Retour aux actualités
          </Link>
        </div>
      </div>
    );
  }

  if (!article) return <div className="min-h-screen bg-mist pt-40" data-testid="article-loading" />;

  const url = window.location.href;
  const shareText = encodeURIComponent(article.title);
  const shareUrl = encodeURIComponent(url);
  const video = youtubeEmbed(article.video_url);
  const fileVideo = !video && article.video_url ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${article.video_url}` : null;

  const copyLink = () => {
    navigator.clipboard?.writeText(url).then(() => toast.success("Lien copié !"));
  };

  return (
    <>
      <SEO title={article.title} description={article.content?.slice(0, 150)} />
      <article data-testid="article-page">
        <header className="bg-navy pt-40 pb-16 sm:pb-20">
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <Link to="/actualites" data-testid="article-back" className="inline-flex items-center gap-2 text-white/60 hover:text-rouge font-heading uppercase tracking-wider text-xs transition-colors duration-300">
              <ArrowLeft size={14} /> Toutes les actualités
            </Link>
            <div className="mt-8 flex items-center gap-3">
              <span className="bg-rouge text-white text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5">{article.category}</span>
              <span className="text-white/50 text-xs uppercase tracking-widest">{fmtDate(article.date)}</span>
            </div>
            <h1 className="mt-6 font-heading uppercase font-semibold tracking-tighter leading-[0.95] text-white text-4xl sm:text-6xl">
              {article.title}
            </h1>
            <p className="mt-6 text-white/50 text-sm">Par {article.author}</p>
          </div>
        </header>

        {article.image && (
          <img
            src={article.image}
            alt=""
            aria-hidden="true"
            className="hidden"
            onLoad={(e) => setPortrait(e.target.naturalHeight > e.target.naturalWidth)}
          />
        )}

        {article.image && !portrait && (
          <div className="max-w-5xl mx-auto px-5 sm:px-8 -mt-2">
            <div className="bg-navy flex items-center justify-center overflow-hidden" data-testid="article-cover">
              <img src={article.image} alt={article.title} className="w-full max-h-[75vh] object-contain" />
            </div>
          </div>
        )}

        <div
          className={
            portrait
              ? "max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-[400px_minmax(0,1fr)] gap-10 lg:gap-16 items-start"
              : "max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20"
          }
        >
          {article.image && portrait && (
            <div className="bg-navy overflow-hidden lg:sticky lg:top-28 self-start" data-testid="article-cover">
              <img src={article.image} alt={article.title} className="w-full h-auto object-contain" />
            </div>
          )}
          <div className="min-w-0">
          <Reveal>
            <div className="prose-like space-y-5 text-navy/85 text-base sm:text-lg leading-relaxed" data-testid="article-content">
              {article.content.split(/\n+/).filter(Boolean).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>

          {video && (
            <div className="mt-12 aspect-video" data-testid="article-video">
              <iframe src={video} title="Vidéo" className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            </div>
          )}

          {fileVideo && (
            <div className="mt-12" data-testid="article-video">
              <video controls preload="metadata" className="w-full bg-navy" src={fileVideo} />
            </div>
          )}

          {article.gallery?.length > 0 && (
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="article-gallery">
              {article.gallery.map((img, i) => (
                <img key={i} src={img} alt={`${article.title} — photo ${i + 1}`} loading="lazy" className="w-full h-40 sm:h-52 object-cover" />
              ))}
            </div>
          )}

          {article.external_url && (
            <div className="mt-12 bg-mist border-l-2 border-rouge p-6 sm:p-8" data-testid="article-external">
              <p className="text-sm text-steel leading-relaxed">
                Vous quittez le site du Club Cycliste Le Boulou pour consulter l'article original
                (ouverture dans un nouvel onglet).
              </p>
              <a
                href={article.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-external-link"
                className="group mt-5 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-navy transition-colors duration-300"
              >
                {article.external_label || "Lire l'article original"}
                <ExternalLink size={15} />
              </a>
            </div>
          )}

          <div className="mt-14 pt-8 border-t border-navy/10">
            <p className="font-heading uppercase tracking-wider text-xs text-steel mb-4">Partager cet article</p>
            <div className="flex gap-3" data-testid="article-share">
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" aria-label="Partager sur Facebook" className="flex h-11 w-11 items-center justify-center border border-navy/20 text-navy hover:bg-navy hover:text-white transition-[background-color,color] duration-300">
                <Facebook size={17} />
              </a>
              <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`} target="_blank" rel="noopener noreferrer" data-testid="share-x" aria-label="Partager sur X" className="flex h-11 w-11 items-center justify-center border border-navy/20 text-navy hover:bg-navy hover:text-white transition-[background-color,color] duration-300">
                <Twitter size={17} />
              </a>
              <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" aria-label="Partager sur WhatsApp" className="flex h-11 w-11 items-center justify-center border border-navy/20 text-navy hover:bg-navy hover:text-white transition-[background-color,color] duration-300">
                <MessageCircle size={17} />
              </a>
              <button onClick={copyLink} data-testid="share-copy" aria-label="Copier le lien" className="flex h-11 w-11 items-center justify-center border border-navy/20 text-navy hover:bg-rouge hover:border-rouge hover:text-white transition-[background-color,color,border-color] duration-300">
                <Link2 size={17} />
              </button>
            </div>
          </div>
          </div>
        </div>
      </article>
    </>
  );
}
